import { useRef, useState, useEffect } from 'react';
import { useStyles } from '../../theme';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { Typography } from '@material-ui/core';

import FormControl from '@mui/material/FormControl';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Select from '@mui/material/Select';
import { ROWS_OUTLINE_ICON } from '../../constants';
import titleize from 'titleize';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

import { useOutsideClickHandler } from '../../hooks/useOutsideClickHandler';

const styles = (theme) => ({
  filterWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    margin: 5,
    marginRight: 20,
    position: 'relative' // Ensure positioning context
  },
  priorityOption: {
    paddingLeft: 15,
    fontSize: 15,
    letterSpacing: 0.25,
  },
  buttonWrapper: {
    display: 'flex',
    justifyContent: 'end',
    margin: "16px 32px 8px"
  },
  applyButton: {
    boxShadow: 'none !important',
    width: 97,
    backgroundColor: '#00BBBB !important',
    color: 'white',
  },
  filterCheckbox: {
    padding: '0 9px',
    margin: '4px',
    color: '#A8A8A8',
    '&.Mui-checked': {
      color: '#00BBBB',
    }
  },
  formGroup: {
    border: 'solid #D2D2D2',
    borderWidth: '1px 0 1px 0',
    padding: '12px 0'
  },
  formBox: {
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'column',
    width: 300
  },
  selectDropdown: {
    minWidth: 200,
    maxHeight: 38,
    backgroundColor: 'white'
  }
});

const StyledCheckbox = ({...props}) => {
    return <Checkbox {...props}
    sx={{
      padding: '0 9px',
      margin: '4px',
      color: '#A8A8A8',
      '&.Mui-checked': {
        color: '#00BBBB',
      },
      '&.MuiCheckbox-indeterminate': {
        color: '#00BBBB',
      },
  }}/>};

const bucketPriorities = [ 'critical', 'high', 'medium', 'low'];

export const BoardPriorityFilter = ({ priorityFilter, onClickApply }) => {
  const [open, setOpen] = useState(false);
  const [priorities, setPriorities] = useState(new Set(bucketPriorities));
  
  const classes = useStyles(styles);

  const outsideClickCb = () => setOpen(false);

  const selectRef = useRef(null);
  const dropdownRef = useRef(null);

  useOutsideClickHandler(
    [selectRef, dropdownRef],
    outsideClickCb
  );
  
  const handleClose = () => {
    setOpen(false);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClickApply = () => {
    if (priorities.size === bucketPriorities.length) {
      onClickApply(null);
    } else {
      onClickApply(Array.from(priorities));
    }
    handleClose();
  };

  const handleSelectAllChange = (event) => {
    setPriorities(event.target.checked ? new Set(bucketPriorities) : new Set());
  };

  const handlePriorityChange = (event) => {
    if (event.target.checked) {
      setPriorities(new Set(priorities).add(event.target.value));
    } else {
      const newPriorities = new Set(priorities);
      newPriorities.delete(event.target.value);
      setPriorities(newPriorities);
    }
  };

  const handleMenuRef = (element) => {
    if (element) {
      dropdownRef.current = element;
    }
  };

  const MenuProps = {
    PaperProps: {
      ref: handleMenuRef // Attach ref to the Paper element (the dropdown content)
    }
  };

  useEffect(() => {
    setPriorities(new Set(priorityFilter || bucketPriorities));
  }, [open, priorityFilter]);

  const selectLabel = () => {
    return (
      <Typography variant="body2" >
        <img
          src={ROWS_OUTLINE_ICON}
          alt="Rows Icon"
          height="16px"
          style={{ verticalAlign: 'text-top', marginRight: 5 }}
        />
        Buckets ({priorityFilter === null ? bucketPriorities.length : priorityFilter.length})
      </Typography>
    )
  };


  const filterCheckbox = (priority) => { 
    return (<StyledCheckbox 
      checked={priorities.has(priority)}
      onChange={handlePriorityChange}
      value={priority}
      />)
  };

  const selectAllCheckbox = <StyledCheckbox 
    checked={priorities.size === bucketPriorities.length}
    indeterminate={priorities.size > 0 && priorities.size < bucketPriorities.length}
    onChange={handleSelectAllChange}
  />;

  return (
    <div
      ref={selectRef}
      className={classes.filterWrapper}
    >
      <Select
        className={classes.selectDropdown}
        value="placeholder"
        open={open}
        onOpen={handleOpen}
        renderValue={selectLabel}
        MenuProps={MenuProps}
        IconComponent={ExpandMoreIcon}
      >
        <Box component="form" className={classes.formBox}>
          <FormControl>
            <FormControlLabel 
              className={classes.priorityOption}
              sx={{ paddingBottom: "7px" }}
              disableTypography={true}
              control={selectAllCheckbox}
              label="Select all"
            />
            <FormGroup className={classes.formGroup} >
              {bucketPriorities.map((priority) => (
                <FormControlLabel 
                  className={classes.priorityOption}
                  disableTypography={true}
                  key={priority}
                  control={filterCheckbox(priority)}
                  label={titleize(priority)}
                />
              ))}
            </FormGroup>
            <div className={classes.buttonWrapper}>
              <Button className={classes.applyButton} variant="contained" onClick={handleClickApply}>
                <Typography variant="body3">Apply</Typography>
              </Button>
            </div>
          </FormControl>
        </Box>
      </Select>
    </div>
  );
};
